import React from 'react';
import PropTypes from 'prop-types';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LabeledField from './LabeledField';
import { STRONG_BORDER_COLOR } from '../highContrastSx';

const ALL_CATEGORIES_VALUE = '';
const ALL_CATEGORIES_TEST_ID = 'search-panel-category-option-all';

const SELECT_SX = {
    flex: 1,
    minWidth: 0,
    fontSize: { xs: '1rem', sm: '0.875rem' },
    color: 'text.primary',
    '& .MuiSelect-select': {
        padding: 0,
        paddingRight: '24px !important',
        minHeight: 'auto',
        backgroundColor: 'transparent',
    },
    '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
    '& .MuiSelect-icon': { color: 'grey.400', right: 0 },
    '&:before, &:after, &:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
};

const MENU_PROPS = {
    disablePortal: true,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: STRONG_BORDER_COLOR,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            maxHeight: 320,
        },
    },
};

const MENU_ITEM_SX = {
    fontSize: { xs: '1rem', sm: '0.875rem' },
};

function CategoryField({
    label, placeholder, value = null, onChange, categories, disabled = false,
}) {
    const selectValue = value === null || value === undefined ? ALL_CATEGORIES_VALUE : String(value);

    return (
        <LabeledField
            label={label}
            renderInput={({ id }) => (
                <Select
                    id={id}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    value={selectValue}
                    onChange={(event) => {
                        const raw = event.target.value;
                        onChange(raw === ALL_CATEGORIES_VALUE ? null : Number(raw));
                    }}
                    disabled={disabled}
                    IconComponent={ExpandMoreIcon}
                    inputProps={{
                        'data-testid': 'search-panel-category',
                        'aria-label': label,
                    }}
                    SelectDisplayProps={{ 'data-testid': 'search-panel-category-trigger' }}
                    MenuProps={MENU_PROPS}
                    sx={SELECT_SX}
                >
                    <MenuItem
                        value={ALL_CATEGORIES_VALUE}
                        sx={MENU_ITEM_SX}
                        data-testid={ALL_CATEGORIES_TEST_ID}
                    >
                        {placeholder}
                    </MenuItem>
                    {categories.map(category => (
                        <MenuItem
                            key={category.id}
                            value={String(category.id)}
                            sx={MENU_ITEM_SX}
                            data-testid={`search-panel-category-option-${category.id}`}
                        >
                            {category.name}
                        </MenuItem>
                    ))}
                </Select>
            )}
        />
    );
}

CategoryField.propTypes = {
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    value: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    categories: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    })).isRequired,
    disabled: PropTypes.bool,
};

export default CategoryField;
